import dayjs from 'dayjs'

import { IRaffleCategory, IRaffleInList } from 'types/prize-draw/raffles'

type RaffleCategoryFields = Pick<IRaffleInList, 'category' | 'categories'>

type RaffleFlags = Pick<IRaffleInList, 'game_type' | 'has_unclaimed_instant_wins' | 'status'> &
  RaffleCategoryFields

// A draw can sit in several categories. Falls back to the legacy single `category` until the
// API fills `categories` for every draw.
export const getRaffleCategories = ({
  categories,
  category,
}: RaffleCategoryFields): IRaffleCategory[] => {
  if (categories?.length) return categories
  return category ? [category] : []
}

export const isInRaffleCategory = (raffle: RaffleCategoryFields, categoryId: number) =>
  getRaffleCategories(raffle).some((category) => category.id === categoryId)

export const isListable = (raffle: Pick<IRaffleInList, 'status'>) =>
  raffle.status !== 'DRAFT' && raffle.status !== 'CANCELLED'

export const isPenaltyShootout = (raffle: Pick<IRaffleInList, 'game_type'>) =>
  raffle.game_type === 'PENALTY_SHOOTOUT'

// Partners can also set up their own "Instant Wins" raffle category; on desktop the Instant
// Wins chip absorbs it so the filter doesn't show two chips for the same thing.
export const isInstantWinsCategory = (category: Pick<IRaffleCategory, 'name'> | undefined) =>
  /^instant\s*wins?$/i.test(category?.name.trim() ?? '')

// Penalty shootouts are always instant wins; other draws count while they still have
// unclaimed instant win prizes or sit in an "Instant Wins" category. Used by the desktop
// Instant Wins filter chip.
export const isInstantWin = (
  raffle: Pick<IRaffleInList, 'game_type' | 'has_unclaimed_instant_wins'> & RaffleCategoryFields,
) =>
  isPenaltyShootout(raffle) ||
  raffle.has_unclaimed_instant_wins ||
  getRaffleCategories(raffle).some(isInstantWinsCategory)

// The mobile bottom nav Instant Wins tab lists penalty shootouts, closed ones included.
export const isListedPenaltyShootout = (raffle: Pick<IRaffleInList, 'game_type' | 'status'>) =>
  isPenaltyShootout(raffle) && isListable(raffle)

export const isDrawClosed = (endAt: string, now = Date.now()): boolean =>
  dayjs(endAt).valueOf() <= now

export const ENDING_SOON_DAYS = 7
export const ENDING_SOON_WINDOW_MS = ENDING_SOON_DAYS * 24 * 60 * 60 * 1000

export const isLiveDraw = (raffle: Pick<IRaffleInList, 'status' | 'end_at'>, now = Date.now()) =>
  raffle.status === 'ACTIVE' && !isDrawClosed(raffle.end_at, now)

export const isEndingSoon = (raffle: Pick<IRaffleInList, 'status' | 'end_at'>, now = Date.now()) =>
  isLiveDraw(raffle, now) && dayjs(raffle.end_at).valueOf() - now <= ENDING_SOON_WINDOW_MS

export const getNextEndingSoonBoundary = (
  raffles: Pick<IRaffleInList, 'status' | 'end_at'>[],
  now = Date.now(),
): number | undefined => {
  const boundaries = raffles
    .filter((raffle) => isLiveDraw(raffle, now))
    .flatMap((raffle) => {
      const closeAt = dayjs(raffle.end_at).valueOf()
      return [closeAt - ENDING_SOON_WINDOW_MS, closeAt]
    })
    .filter((at) => at > now)

  return boundaries.length ? Math.min(...boundaries) : undefined
}

export const sortByClosingTime = (
  a: Pick<IRaffleInList, 'end_at'>,
  b: Pick<IRaffleInList, 'end_at'>,
  now = Date.now(),
) => {
  const aClosed = isDrawClosed(a.end_at, now)
  const bClosed = isDrawClosed(b.end_at, now)

  if (aClosed && !bClosed) return 1
  if (!aClosed && bClosed) return -1

  return dayjs(a.end_at).valueOf() - dayjs(b.end_at).valueOf()
}

/**
 * The main prize draw list, the same on every screen size. Penalty shootouts live only under
 * the Instant Wins tab/chip; other draws with instant win prizes stay listed in their category
 * too, since they can still be entered as regular draws.
 */
export const getPrizeDrawRaffles = <T extends RaffleFlags & Pick<IRaffleInList, 'end_at'>>(
  raffles: T[],
  now = Date.now(),
): T[] =>
  raffles
    .filter((raffle) => isListable(raffle) && !isPenaltyShootout(raffle))
    .sort((a, b) => sortByClosingTime(a, b, now))

/**
 * The Instant Wins list, closed games included: on desktop every instant win game; on mobile
 * only the penalty shootouts the bottom nav tab advertises.
 */
export const getInstantWinRaffles = <T extends RaffleFlags & Pick<IRaffleInList, 'end_at'>>(
  raffles: T[],
  isDesktop: boolean,
  now = Date.now(),
): T[] =>
  raffles
    .filter((raffle) =>
      isDesktop ? isListable(raffle) && isInstantWin(raffle) : isListedPenaltyShootout(raffle),
    )
    .sort((a, b) => sortByClosingTime(a, b, now))

/**
 * Category chips for the filter: only categories with at least one live prize draw, so
 * categories holding nothing, only closed draws or only penalty shootouts are hidden.
 */
export const getPrizeDrawCategories = (
  categories: IRaffleCategory[],
  prizeDrawRaffles: (Pick<IRaffleInList, 'status' | 'end_at'> & RaffleCategoryFields)[],
  isDesktop: boolean,
  now = Date.now(),
) => {
  const liveCategoryIds = new Set(
    prizeDrawRaffles
      .filter((raffle) => isLiveDraw(raffle, now))
      .flatMap((raffle) => getRaffleCategories(raffle).map((category) => category.id)),
  )

  return categories.filter(
    (category) =>
      !(isDesktop && isInstantWinsCategory(category)) && liveCategoryIds.has(category.id),
  )
}
